import { resolveGameConfig } from "./config.js";
import { hashGameState } from "./hash.js";
import { add, clamp, clampMagnitude, deterministicRound, dot, length, lengthSquared, normalize, oppositePlayer, roundVector, scale, subtract, } from "./math.js";
import { createSnapshotMessage } from "./protocol.js";
import { FIXED_DT_SECONDS, PHYSICS_VERSION, PLAYER_SLOTS, PROTOCOL_VERSION, } from "./types.js";
function cloneVector(vector) {
    return { x: vector.x, y: vector.y };
}
export function cloneGameState(state) {
    return {
        ...state,
        score: { ...state.score },
        players: {
            PLAYER_A: { ...state.players.PLAYER_A },
            PLAYER_B: { ...state.players.PLAYER_B },
        },
        mallets: {
            PLAYER_A: {
                ...state.mallets.PLAYER_A,
                position: cloneVector(state.mallets.PLAYER_A.position),
                previousPosition: cloneVector(state.mallets.PLAYER_A.previousPosition),
                velocity: cloneVector(state.mallets.PLAYER_A.velocity),
                target: cloneVector(state.mallets.PLAYER_A.target),
            },
            PLAYER_B: {
                ...state.mallets.PLAYER_B,
                position: cloneVector(state.mallets.PLAYER_B.position),
                previousPosition: cloneVector(state.mallets.PLAYER_B.previousPosition),
                velocity: cloneVector(state.mallets.PLAYER_B.velocity),
                target: cloneVector(state.mallets.PLAYER_B.target),
            },
        },
        puck: {
            ...state.puck,
            position: cloneVector(state.puck.position),
            velocity: cloneVector(state.puck.velocity),
        },
        lastGoal: state.lastGoal === null
            ? null
            : { ...state.lastGoal, puckPosition: cloneVector(state.lastGoal.puckPosition) },
    };
}
function initialMalletPosition(player, config) {
    return {
        x: config.table.width / 2,
        y: player === "PLAYER_A" ? config.table.height * 0.75 : config.table.height * 0.25,
    };
}
function createMallet(player, config) {
    const position = initialMalletPosition(player, config);
    return {
        position: cloneVector(position),
        previousPosition: cloneVector(position),
        velocity: { x: 0, y: 0 },
        target: cloneVector(position),
        radius: config.mallet.radius,
        mass: config.mallet.mass,
    };
}
export function createInitialState(options = {}) {
    const config = resolveGameConfig(options.config);
    const requestedPhase = options.phase ?? "waiting";
    const phase = requestedPhase === "paused" ? "waiting" : requestedPhase;
    const phaseTicksRemaining = phase === "countdown" ? config.rules.initialCountdownTicks : 0;
    const seed = options.seed ?? 1;
    if (!Number.isSafeInteger(seed) || seed < 0) {
        throw new RangeError("seed must be a non-negative safe integer");
    }
    return {
        matchId: options.matchId ?? "local-match",
        seed,
        physicsVersion: PHYSICS_VERSION,
        protocolVersion: PROTOCOL_VERSION,
        tick: 0,
        phase,
        pausedFromPhase: null,
        phaseTicksRemaining,
        remainingTicks: config.rules.mode === "timed" ? config.rules.durationTicks : null,
        score: { PLAYER_A: 0, PLAYER_B: 0 },
        winner: null,
        finishReason: null,
        players: {
            PLAYER_A: { connected: false, ready: false, lastProcessedSequence: -1 },
            PLAYER_B: { connected: false, ready: false, lastProcessedSequence: -1 },
        },
        mallets: {
            PLAYER_A: createMallet("PLAYER_A", config),
            PLAYER_B: createMallet("PLAYER_B", config),
        },
        puck: {
            position: { x: config.table.width / 2, y: config.table.height / 2 },
            velocity: { x: 0, y: 0 },
            radius: config.puck.radius,
            mass: config.puck.mass,
            active: phase === "playing",
            lastTouchedBy: null,
            lastCollisionTick: null,
            stationaryTicks: 0,
        },
        lastGoal: null,
    };
}
function goalGeometryFromConfig(config) {
    const centerX = config.table.width / 2;
    const openingLeftX = centerX - config.table.goalWidth / 2;
    const openingRightX = centerX + config.table.goalWidth / 2;
    const topLineY = config.table.wallInset;
    const bottomLineY = config.table.height - config.table.wallInset;
    const leftPostX = openingLeftX - config.table.postRadius;
    const rightPostX = openingRightX + config.table.postRadius;
    return {
        topLineY,
        bottomLineY,
        openingLeftX,
        openingRightX,
        posts: [
            { x: leftPostX, y: topLineY },
            { x: rightPostX, y: topLineY },
            { x: leftPostX, y: bottomLineY },
            { x: rightPostX, y: bottomLineY },
        ],
    };
}
export function getGoalGeometry(configInput = {}) {
    return goalGeometryFromConfig(resolveGameConfig(configInput));
}
function malletBoundsFromConfig(player, config) {
    const radius = config.mallet.radius;
    const middleY = config.table.height / 2;
    const halfGap = config.mallet.centerLineGap / 2;
    return {
        minimumX: config.table.wallInset + radius,
        maximumX: config.table.width - config.table.wallInset - radius,
        minimumY: player === "PLAYER_A"
            ? middleY + halfGap + radius
            : config.table.wallInset + radius,
        maximumY: player === "PLAYER_A"
            ? config.table.height - config.table.wallInset - radius
            : middleY - halfGap - radius,
    };
}
export function getMalletBounds(player, configInput = {}) {
    return malletBoundsFromConfig(player, resolveGameConfig(configInput));
}
function clampMalletTarget(target, player, config) {
    const bounds = malletBoundsFromConfig(player, config);
    let clampedTarget = {
        x: clamp(target.x, bounds.minimumX, bounds.maximumX),
        y: clamp(target.y, bounds.minimumY, bounds.maximumY),
    };
    const effectiveRadius = config.table.cornerRadius - config.mallet.radius;
    for (const corner of getCornerDefinitions(config)) {
        if (!isInCornerQuadrant(clampedTarget, corner, config.physics.collisionEpsilon))
            continue;
        const fromCenter = subtract(clampedTarget, corner.center);
        const distance = length(fromCenter);
        if (distance > effectiveRadius) {
            clampedTarget = add(corner.center, scale(fromCenter, effectiveRadius / distance));
        }
    }
    return clampedTarget;
}
function resetBodiesMutable(state, config) {
    for (const player of PLAYER_SLOTS) {
        const start = initialMalletPosition(player, config);
        const mallet = state.mallets[player];
        mallet.position = cloneVector(start);
        mallet.previousPosition = cloneVector(start);
        mallet.velocity = { x: 0, y: 0 };
        mallet.target = cloneVector(start);
    }
    state.puck.position = { x: config.table.width / 2, y: config.table.height / 2 };
    state.puck.velocity = { x: 0, y: 0 };
    state.puck.active = false;
    state.puck.lastTouchedBy = null;
    state.puck.lastCollisionTick = null;
    state.puck.stationaryTicks = 0;
}
export function resetRound(current, configInput = {}) {
    const config = resolveGameConfig(configInput);
    const state = cloneGameState(current);
    resetBodiesMutable(state, config);
    state.lastGoal = null;
    state.phase = config.rules.restartCountdownTicks === 0 ? "playing" : "countdown";
    state.phaseTicksRemaining = config.rules.restartCountdownTicks;
    state.puck.active = state.phase === "playing";
    state.pausedFromPhase = null;
    return state;
}
export function resetMatch(current, configInput = {}) {
    return createInitialState({
        matchId: current.matchId,
        seed: current.seed,
        config: configInput,
    });
}
export function setPlayerConnected(current, player, connected) {
    const state = cloneGameState(current);
    state.players[player].connected = connected;
    if (!connected)
        state.players[player].ready = false;
    return state;
}
export function setPlayerReady(current, player, ready = true, configInput = {}) {
    const config = resolveGameConfig(configInput);
    const state = cloneGameState(current);
    if (state.phase === "finished")
        return state;
    state.players[player].ready = ready;
    if (state.phase === "waiting" || state.phase === "ready" || state.phase === "countdown") {
        const bothReady = state.players.PLAYER_A.ready && state.players.PLAYER_B.ready;
        if (bothReady) {
            if (state.phase !== "countdown") {
                state.phase = config.rules.initialCountdownTicks === 0 ? "playing" : "countdown";
                state.phaseTicksRemaining = config.rules.initialCountdownTicks;
                state.puck.active = state.phase === "playing";
            }
        }
        else {
            state.phase = state.players.PLAYER_A.ready || state.players.PLAYER_B.ready ? "ready" : "waiting";
            state.phaseTicksRemaining = 0;
            state.puck.active = false;
        }
    }
    return state;
}
export function pauseMatch(current) {
    const state = cloneGameState(current);
    if (state.phase !== "paused" && state.phase !== "finished") {
        state.pausedFromPhase = state.phase;
        state.phase = "paused";
        state.puck.active = false;
    }
    return state;
}
export function resumeMatch(current) {
    const state = cloneGameState(current);
    if (state.phase === "paused") {
        const resumePhase = state.pausedFromPhase ?? "countdown";
        state.phase = resumePhase;
        state.pausedFromPhase = null;
        state.puck.active = resumePhase === "playing";
    }
    return state;
}
export function forfeitMatch(current, forfeitingPlayer) {
    const state = cloneGameState(current);
    if (state.phase === "finished")
        return state;
    state.phase = "finished";
    state.phaseTicksRemaining = 0;
    state.pausedFromPhase = null;
    state.puck.active = false;
    state.winner = oppositePlayer(forfeitingPlayer);
    state.finishReason = "forfeit";
    return state;
}
export function cancelMatch(current) {
    const state = cloneGameState(current);
    if (state.phase === "finished")
        return state;
    state.phase = "finished";
    state.phaseTicksRemaining = 0;
    state.pausedFromPhase = null;
    state.puck.active = false;
    state.winner = null;
    state.finishReason = "cancelled";
    return state;
}
function copyInput(input) {
    const base = {
        player: input.player,
        sequence: input.sequence,
        targetX: input.targetX,
        targetY: input.targetY,
    };
    return input.clientTime === undefined ? base : { ...base, clientTime: input.clientTime };
}
function compareInputs(a, b) {
    if (a.player !== b.player)
        return a.player === "PLAYER_A" ? -1 : 1;
    return a.sequence - b.sequence;
}
function processInputs(state, inputs, config) {
    const accepted = [];
    const rejected = [];
    const ordered = inputs.map(copyInput).sort(compareInputs);
    for (const input of ordered) {
        let code = null;
        if (input.player !== "PLAYER_A" && input.player !== "PLAYER_B") {
            code = "INVALID_PLAYER";
        }
        else if (!Number.isSafeInteger(input.sequence) || input.sequence < 0) {
            code = "INVALID_SEQUENCE";
        }
        else if (input.sequence <= state.players[input.player].lastProcessedSequence) {
            code = "STALE_SEQUENCE";
        }
        else if (!Number.isFinite(input.targetX) ||
            !Number.isFinite(input.targetY) ||
            input.targetX < 0 ||
            input.targetX > 1 ||
            input.targetY < 0 ||
            input.targetY > 1 ||
            (input.clientTime !== undefined && !Number.isFinite(input.clientTime))) {
            code = "INVALID_TARGET";
        }
        else if (state.phase === "finished") {
            code = "MATCH_FINISHED";
        }
        if (code !== null) {
            rejected.push({ input, code });
            continue;
        }
        state.players[input.player].lastProcessedSequence = input.sequence;
        state.mallets[input.player].target = clampMalletTarget({ x: input.targetX * config.table.width, y: input.targetY * config.table.height }, input.player, config);
        accepted.push(input);
    }
    return { accepted, rejected };
}
function updateMallet(mallet, player, config) {
    const start = cloneVector(mallet.position);
    mallet.previousPosition = start;
    const toTarget = subtract(mallet.target, start);
    const desiredVelocity = clampMagnitude(scale(toTarget, 1 / FIXED_DT_SECONDS), config.mallet.maxSpeed);
    const velocityDelta = clampMagnitude(subtract(desiredVelocity, mallet.velocity), config.mallet.maxAcceleration * FIXED_DT_SECONDS);
    const controlledVelocity = clampMagnitude(add(mallet.velocity, velocityDelta), config.mallet.maxSpeed);
    const proposedPosition = add(start, scale(controlledVelocity, FIXED_DT_SECONDS));
    const clampedPosition = clampMalletTarget(proposedPosition, player, config);
    mallet.position = clampedPosition;
    mallet.velocity = scale(subtract(clampedPosition, start), 1 / FIXED_DT_SECONDS);
}
function shouldMoveMallets(phase) {
    return phase === "waiting" || phase === "ready" || phase === "countdown" || phase === "playing";
}
function movingCircleTime(relativePosition, relativeVelocity, combinedRadius, maximumTime, epsilon) {
    const c = lengthSquared(relativePosition) - combinedRadius * combinedRadius;
    const approach = dot(relativePosition, relativeVelocity);
    if (c <= epsilon) {
        if (c < -epsilon || approach < 0)
            return 0;
        return null;
    }
    const a = lengthSquared(relativeVelocity);
    if (a <= epsilon || approach >= 0)
        return null;
    const discriminant = approach * approach - a * c;
    if (discriminant < 0)
        return null;
    const time = (-approach - Math.sqrt(discriminant)) / a;
    if (time < -epsilon || time > maximumTime + epsilon)
        return null;
    return clamp(time, 0, maximumTime);
}
function collisionPriority(candidate) {
    switch (candidate.kind) {
        case "mallet": return 0;
        case "post": return 1;
        case "corner": return 2;
        case "wall": return 2;
        case "goal": return 3;
    }
}
function chooseEarlier(current, candidate, epsilon) {
    if (current === null || candidate.time < current.time - epsilon)
        return candidate;
    if (Math.abs(candidate.time - current.time) <= epsilon && collisionPriority(candidate) < collisionPriority(current)) {
        return candidate;
    }
    return current;
}
function wallCandidate(puckPosition, puckVelocity, radius, maximumTime, config, geometry) {
    let earliest = null;
    const epsilon = config.physics.collisionEpsilon;
    const left = config.table.wallInset + radius;
    const right = config.table.width - config.table.wallInset - radius;
    const top = geometry.topLineY + radius;
    const bottom = geometry.bottomLineY - radius;
    const straightTop = geometry.topLineY + config.table.cornerRadius;
    const straightBottom = geometry.bottomLineY - config.table.cornerRadius;
    const straightLeft = config.table.wallInset + config.table.cornerRadius;
    const straightRight = config.table.width - config.table.wallInset - config.table.cornerRadius;
    const onVerticalStraight = puckPosition.y >= straightTop - epsilon && puckPosition.y <= straightBottom + epsilon;
    const onSolidHorizontalStraight = puckPosition.x >= straightLeft - epsilon &&
        puckPosition.x <= straightRight + epsilon &&
        (puckPosition.x <= geometry.openingLeftX || puckPosition.x >= geometry.openingRightX);
    // A mallet separation can project an overlapping puck through a boundary.
    // Repair that penetration before looking for a future swept crossing.
    if (puckPosition.x < left - epsilon && onVerticalStraight) {
        earliest = { kind: "wall", time: 0, side: "left", normal: { x: 1, y: 0 } };
    }
    else if (puckVelocity.x < -epsilon) {
        const time = (left - puckPosition.x) / puckVelocity.x;
        if (time >= -epsilon && time <= maximumTime + epsilon) {
            const impactY = puckPosition.y + puckVelocity.y * time;
            if (impactY >= straightTop && impactY <= straightBottom) {
                earliest = chooseEarlier(earliest, { kind: "wall", time: clamp(time, 0, maximumTime), side: "left", normal: { x: 1, y: 0 } }, epsilon);
            }
        }
    }
    if (puckPosition.x > right + epsilon && onVerticalStraight) {
        earliest = chooseEarlier(earliest, { kind: "wall", time: 0, side: "right", normal: { x: -1, y: 0 } }, epsilon);
    }
    else if (puckVelocity.x > epsilon) {
        const time = (right - puckPosition.x) / puckVelocity.x;
        if (time >= -epsilon && time <= maximumTime + epsilon) {
            const impactY = puckPosition.y + puckVelocity.y * time;
            if (impactY >= straightTop && impactY <= straightBottom) {
                earliest = chooseEarlier(earliest, { kind: "wall", time: clamp(time, 0, maximumTime), side: "right", normal: { x: -1, y: 0 } }, epsilon);
            }
        }
    }
    if (puckPosition.y < top - epsilon && onSolidHorizontalStraight) {
        earliest = chooseEarlier(earliest, { kind: "wall", time: 0, side: "top", normal: { x: 0, y: 1 } }, epsilon);
    }
    else if (puckVelocity.y < -epsilon) {
        const time = (top - puckPosition.y) / puckVelocity.y;
        if (time >= -epsilon && time <= maximumTime + epsilon) {
            const impactX = puckPosition.x + puckVelocity.x * time;
            if (impactX >= straightLeft &&
                impactX <= straightRight &&
                (impactX <= geometry.openingLeftX || impactX >= geometry.openingRightX)) {
                earliest = chooseEarlier(earliest, { kind: "wall", time: clamp(time, 0, maximumTime), side: "top", normal: { x: 0, y: 1 } }, epsilon);
            }
        }
    }
    if (puckPosition.y > bottom + epsilon && onSolidHorizontalStraight) {
        earliest = chooseEarlier(earliest, { kind: "wall", time: 0, side: "bottom", normal: { x: 0, y: -1 } }, epsilon);
    }
    else if (puckVelocity.y > epsilon) {
        const time = (bottom - puckPosition.y) / puckVelocity.y;
        if (time >= -epsilon && time <= maximumTime + epsilon) {
            const impactX = puckPosition.x + puckVelocity.x * time;
            if (impactX >= straightLeft &&
                impactX <= straightRight &&
                (impactX <= geometry.openingLeftX || impactX >= geometry.openingRightX)) {
                earliest = chooseEarlier(earliest, { kind: "wall", time: clamp(time, 0, maximumTime), side: "bottom", normal: { x: 0, y: -1 } }, epsilon);
            }
        }
    }
    return earliest;
}
function getCornerDefinitions(config) {
    const left = config.table.wallInset;
    const right = config.table.width - config.table.wallInset;
    const top = config.table.wallInset;
    const bottom = config.table.height - config.table.wallInset;
    const radius = config.table.cornerRadius;
    return [
        { quadrant: "top-left", center: { x: left + radius, y: top + radius } },
        { quadrant: "top-right", center: { x: right - radius, y: top + radius } },
        { quadrant: "bottom-left", center: { x: left + radius, y: bottom - radius } },
        { quadrant: "bottom-right", center: { x: right - radius, y: bottom - radius } },
    ];
}
function isInCornerQuadrant(position, corner, epsilon) {
    switch (corner.quadrant) {
        case "top-left":
            return position.x <= corner.center.x + epsilon && position.y <= corner.center.y + epsilon;
        case "top-right":
            return position.x >= corner.center.x - epsilon && position.y <= corner.center.y + epsilon;
        case "bottom-left":
            return position.x <= corner.center.x + epsilon && position.y >= corner.center.y - epsilon;
        case "bottom-right":
            return position.x >= corner.center.x - epsilon && position.y >= corner.center.y - epsilon;
    }
}
function sweptCornerExitTime(position, velocity, corner, effectiveRadius, maximumTime, epsilon) {
    const relative = subtract(position, corner.center);
    const a = lengthSquared(velocity);
    if (a <= epsilon)
        return null;
    const b = dot(relative, velocity);
    const c = lengthSquared(relative) - effectiveRadius * effectiveRadius;
    const discriminant = b * b - a * c;
    if (discriminant < 0)
        return null;
    const time = (-b + Math.sqrt(discriminant)) / a;
    if (time < -epsilon || time > maximumTime + epsilon)
        return null;
    const clampedTime = clamp(time, 0, maximumTime);
    const impact = add(position, scale(velocity, clampedTime));
    return isInCornerQuadrant(impact, corner, epsilon) ? clampedTime : null;
}
function cornerCandidate(position, velocity, radius, maximumTime, config) {
    const epsilon = config.physics.collisionEpsilon;
    const effectiveRadius = config.table.cornerRadius - radius;
    let earliest = null;
    for (const corner of getCornerDefinitions(config)) {
        const relative = subtract(position, corner.center);
        if (isInCornerQuadrant(position, corner, epsilon) &&
            lengthSquared(relative) > effectiveRadius * effectiveRadius + epsilon) {
            earliest = chooseEarlier(earliest, { kind: "corner", time: 0, corner }, epsilon);
            continue;
        }
        const time = sweptCornerExitTime(position, velocity, corner, effectiveRadius, maximumTime, epsilon);
        if (time !== null) {
            earliest = chooseEarlier(earliest, { kind: "corner", time, corner }, epsilon);
        }
    }
    return earliest;
}
function isBetweenGoalPosts(x, geometry) {
    return x >= geometry.openingLeftX && x <= geometry.openingRightX;
}
function goalCandidate(position, velocity, radius, maximumTime, config, geometry) {
    const epsilon = config.physics.collisionEpsilon;
    if (velocity.y < -epsilon) {
        const threshold = geometry.topLineY - radius;
        const crossingTime = (threshold - position.y) / velocity.y;
        if (crossingTime >= -epsilon && crossingTime < maximumTime - epsilon) {
            const time = Math.max(0, crossingTime + epsilon);
            const x = position.x + velocity.x * time;
            if (isBetweenGoalPosts(x, geometry))
                return { kind: "goal", time, scorer: "PLAYER_A" };
        }
    }
    if (velocity.y > epsilon) {
        const threshold = geometry.bottomLineY + radius;
        const crossingTime = (threshold - position.y) / velocity.y;
        if (crossingTime >= -epsilon && crossingTime < maximumTime - epsilon) {
            const time = Math.max(0, crossingTime + epsilon);
            const x = position.x + velocity.x * time;
            if (isBetweenGoalPosts(x, geometry))
                return { kind: "goal", time, scorer: "PLAYER_B" };
        }
    }
    return null;
}
function findEarliestCollision(state, elapsed, remaining, config, geometry) {
    const puck = state.puck;
    const epsilon = config.physics.collisionEpsilon;
    let earliest = wallCandidate(puck.position, puck.velocity, puck.radius, remaining, config, geometry);
    const corner = cornerCandidate(puck.position, puck.velocity, puck.radius, remaining, config);
    if (corner !== null)
        earliest = chooseEarlier(earliest, corner, epsilon);
    const goal = goalCandidate(puck.position, puck.velocity, puck.radius, remaining, config, geometry);
    if (goal !== null)
        earliest = chooseEarlier(earliest, goal, epsilon);
    for (const center of geometry.posts) {
        const time = movingCircleTime(subtract(puck.position, center), puck.velocity, puck.radius + config.table.postRadius, remaining, epsilon);
        if (time !== null)
            earliest = chooseEarlier(earliest, { kind: "post", time, center }, epsilon);
    }
    for (const player of PLAYER_SLOTS) {
        const mallet = state.mallets[player];
        const malletAtElapsed = add(mallet.previousPosition, scale(mallet.velocity, elapsed));
        const time = movingCircleTime(subtract(puck.position, malletAtElapsed), subtract(puck.velocity, mallet.velocity), puck.radius + mallet.radius, remaining, epsilon);
        if (time !== null)
            earliest = chooseEarlier(earliest, { kind: "mallet", time, player }, epsilon);
    }
    return earliest;
}
function capPuckSpeed(state, config) {
    state.puck.velocity = clampMagnitude(state.puck.velocity, config.puck.maxSpeed);
}
function resolveCollision(state, collision, elapsed, config) {
    const puck = state.puck;
    if (collision.kind === "wall") {
        const normalSpeed = dot(puck.velocity, collision.normal);
        if (normalSpeed < 0) {
            puck.velocity = subtract(puck.velocity, scale(collision.normal, (1 + config.puck.wallRestitution) * normalSpeed));
        }
        const boundary = collision.side === "left"
            ? config.table.wallInset + puck.radius
            : collision.side === "right"
                ? config.table.width - config.table.wallInset - puck.radius
                : collision.side === "top"
                    ? config.table.wallInset + puck.radius
                    : config.table.height - config.table.wallInset - puck.radius;
        if (collision.side === "left" || collision.side === "right")
            puck.position.x = boundary;
        else
            puck.position.y = boundary;
        capPuckSpeed(state, config);
        return { type: "COLLISION", tick: state.tick, collider: "wall" };
    }
    if (collision.kind === "corner") {
        const outward = normalize(subtract(puck.position, collision.corner.center));
        const normal = scale(outward, -1);
        const effectiveRadius = config.table.cornerRadius - puck.radius;
        puck.position = add(collision.corner.center, scale(outward, effectiveRadius - config.physics.collisionEpsilon));
        const normalSpeed = dot(puck.velocity, normal);
        if (normalSpeed < 0) {
            puck.velocity = subtract(puck.velocity, scale(normal, (1 + config.puck.wallRestitution) * normalSpeed));
        }
        capPuckSpeed(state, config);
        return { type: "COLLISION", tick: state.tick, collider: "wall" };
    }
    if (collision.kind === "post") {
        const normal = normalize(subtract(puck.position, collision.center), normalize(scale(puck.velocity, -1)));
        puck.position = add(collision.center, scale(normal, puck.radius + config.table.postRadius + config.physics.collisionEpsilon));
        const normalSpeed = dot(puck.velocity, normal);
        if (normalSpeed < 0) {
            puck.velocity = subtract(puck.velocity, scale(normal, (1 + config.puck.postRestitution) * normalSpeed));
        }
        capPuckSpeed(state, config);
        return { type: "COLLISION", tick: state.tick, collider: "post" };
    }
    const mallet = state.mallets[collision.player];
    const malletPosition = add(mallet.previousPosition, scale(mallet.velocity, elapsed));
    const normal = normalize(subtract(puck.position, malletPosition), normalize(subtract(puck.velocity, mallet.velocity)));
    puck.position = add(malletPosition, scale(normal, puck.radius + mallet.radius + config.physics.collisionEpsilon));
    const relativeNormalSpeed = dot(subtract(puck.velocity, mallet.velocity), normal);
    if (relativeNormalSpeed < 0) {
        const impulsePerPuckMass = -(1 + config.puck.malletRestitution) *
            relativeNormalSpeed *
            (mallet.mass / (mallet.mass + puck.mass));
        puck.velocity = add(puck.velocity, scale(normal, impulsePerPuckMass));
    }
    puck.lastTouchedBy = collision.player;
    capPuckSpeed(state, config);
    return { type: "COLLISION", tick: state.tick, collider: "mallet", player: collision.player };
}
function detectAlreadyCrossedGoal(state, geometry) {
    const { position, radius } = state.puck;
    if (!isBetweenGoalPosts(position.x, geometry))
        return null;
    if (position.y + radius < geometry.topLineY)
        return "PLAYER_A";
    if (position.y - radius > geometry.bottomLineY)
        return "PLAYER_B";
    return null;
}
function recoverPuckInsideTable(state, config, geometry) {
    const puck = state.puck;
    const epsilon = config.physics.collisionEpsilon;
    const left = config.table.wallInset + puck.radius;
    const right = config.table.width - config.table.wallInset - puck.radius;
    const top = geometry.topLineY + puck.radius;
    const bottom = geometry.bottomLineY - puck.radius;
    const straightTop = geometry.topLineY + config.table.cornerRadius;
    const straightBottom = geometry.bottomLineY - config.table.cornerRadius;
    const straightLeft = config.table.wallInset + config.table.cornerRadius;
    const straightRight = config.table.width - config.table.wallInset - config.table.cornerRadius;
    if (puck.position.y >= straightTop && puck.position.y <= straightBottom) {
        puck.position.x = clamp(puck.position.x, left, right);
    }
    if (puck.position.x >= straightLeft &&
        puck.position.x <= straightRight &&
        (puck.position.x <= geometry.openingLeftX || puck.position.x >= geometry.openingRightX)) {
        puck.position.y = clamp(puck.position.y, top, bottom);
    }
    const effectiveRadius = config.table.cornerRadius - puck.radius;
    for (const corner of getCornerDefinitions(config)) {
        if (!isInCornerQuadrant(puck.position, corner, epsilon))
            continue;
        const relative = subtract(puck.position, corner.center);
        if (lengthSquared(relative) <= effectiveRadius * effectiveRadius)
            continue;
        puck.position = add(corner.center, scale(normalize(relative), effectiveRadius - epsilon));
    }
}
function simulatePuck(state, config) {
    const puck = state.puck;
    const collisions = [];
    const geometry = goalGeometryFromConfig(config);
    const frictionFactor = Math.max(0, 1 - config.puck.frictionPerSecond * FIXED_DT_SECONDS);
    puck.velocity = scale(puck.velocity, frictionFactor);
    capPuckSpeed(state, config);
    const alreadyCrossed = detectAlreadyCrossedGoal(state, geometry);
    if (alreadyCrossed !== null)
        return { scorer: alreadyCrossed, collisions };
    let elapsed = 0;
    let remaining = FIXED_DT_SECONDS;
    let collisionCount = 0;
    while (remaining > config.physics.collisionEpsilon && collisionCount < config.physics.maxCollisionsPerTick) {
        const candidate = findEarliestCollision(state, elapsed, remaining, config, geometry);
        if (candidate === null) {
            puck.position = add(puck.position, scale(puck.velocity, remaining));
            elapsed += remaining;
            remaining = 0;
            break;
        }
        if (candidate.time > 0) {
            puck.position = add(puck.position, scale(puck.velocity, candidate.time));
            elapsed += candidate.time;
            remaining -= candidate.time;
        }
        if (candidate.kind === "goal")
            return { scorer: candidate.scorer, collisions };
        collisions.push(resolveCollision(state, candidate, elapsed, config));
        puck.lastCollisionTick = state.tick;
        collisionCount += 1;
        const nudgeTime = Math.min(config.physics.collisionEpsilon, remaining);
        if (nudgeTime > 0) {
            puck.position = add(puck.position, scale(puck.velocity, nudgeTime));
            elapsed += nudgeTime;
            remaining -= nudgeTime;
        }
    }
    if (remaining > config.physics.collisionEpsilon &&
        collisionCount >= config.physics.maxCollisionsPerTick) {
        // A simultaneous pinch can create more zero-time contacts than the bounded
        // solver may process. Never integrate unresolved time through a wall.
        recoverPuckInsideTable(state, config, geometry);
    }
    else if (remaining > 0) {
        puck.position = add(puck.position, scale(puck.velocity, remaining));
    }
    return { scorer: detectAlreadyCrossedGoal(state, geometry), collisions };
}
function isWinningScore(state, scorer, config) {
    if (config.rules.mode === "sudden-death")
        return true;
    return config.rules.mode === "classic" && state.score[scorer] >= config.rules.pointsToWin;
}
function setFinishedMutable(state, winner, reason) {
    state.phase = "finished";
    state.phaseTicksRemaining = 0;
    state.pausedFromPhase = null;
    state.puck.active = false;
    state.winner = winner;
    state.finishReason = reason;
}
function roundStateMutable(state, config) {
    const precision = config.physics.statePrecision;
    for (const player of PLAYER_SLOTS) {
        const mallet = state.mallets[player];
        mallet.position = roundVector(mallet.position, precision);
        mallet.previousPosition = roundVector(mallet.previousPosition, precision);
        mallet.velocity = roundVector(mallet.velocity, precision);
        mallet.target = roundVector(mallet.target, precision);
    }
    state.puck.position = roundVector(state.puck.position, precision);
    state.puck.velocity = roundVector(state.puck.velocity, precision);
    if (state.lastGoal !== null)
        state.lastGoal.puckPosition = roundVector(state.lastGoal.puckPosition, precision);
    state.phaseTicksRemaining = Math.max(0, Math.round(state.phaseTicksRemaining));
    state.remainingTicks = state.remainingTicks === null ? null : Math.max(0, Math.round(state.remainingTicks));
    state.seed = Math.round(deterministicRound(state.seed, 1));
}
function transition(state, to, events) {
    const from = state.phase;
    state.phase = to;
    if (from !== to)
        events.push({ type: "PHASE_CHANGED", tick: state.tick, from, to });
}
export function stepGame(current, inputs = [], configInput = {}) {
    const config = resolveGameConfig(configInput);
    const state = cloneGameState(current);
    const events = [];
    state.tick += 1;
    const processed = processInputs(state, inputs, config);
    if (shouldMoveMallets(state.phase)) {
        for (const player of PLAYER_SLOTS)
            updateMallet(state.mallets[player], player, config);
    }
    else {
        for (const player of PLAYER_SLOTS) {
            state.mallets[player].previousPosition = cloneVector(state.mallets[player].position);
            state.mallets[player].velocity = { x: 0, y: 0 };
        }
    }
    let scoredBy = null;
    let finishedThisTick = null;
    if (state.phase === "countdown") {
        if (state.phaseTicksRemaining > 0)
            state.phaseTicksRemaining -= 1;
        if (state.phaseTicksRemaining === 0) {
            transition(state, "playing", events);
            state.puck.active = true;
        }
    }
    else if (state.phase === "goal") {
        if (state.phaseTicksRemaining > 0)
            state.phaseTicksRemaining -= 1;
        if (state.phaseTicksRemaining === 0) {
            resetBodiesMutable(state, config);
            const next = config.rules.restartCountdownTicks === 0 ? "playing" : "countdown";
            transition(state, next, events);
            state.phaseTicksRemaining = config.rules.restartCountdownTicks;
            state.puck.active = next === "playing";
            events.push({ type: "PUCK_RESET", tick: state.tick, reason: "goal" });
        }
    }
    else if (state.phase === "playing") {
        state.puck.active = true;
        const physics = simulatePuck(state, config);
        events.push(...physics.collisions);
        scoredBy = physics.scorer;
        if (state.remainingTicks !== null && state.remainingTicks > 0)
            state.remainingTicks -= 1;
        if (scoredBy !== null) {
            state.score[scoredBy] += 1;
            state.lastGoal = { scorer: scoredBy, tick: state.tick, puckPosition: cloneVector(state.puck.position) };
            state.puck.active = false;
            if (isWinningScore(state, scoredBy, config)) {
                setFinishedMutable(state, scoredBy, "score");
                finishedThisTick = "score";
            }
            else {
                transition(state, "goal", events);
                state.phaseTicksRemaining = config.rules.goalPauseTicks;
            }
        }
        if (finishedThisTick === null && state.remainingTicks === 0) {
            const winner = state.score.PLAYER_A === state.score.PLAYER_B
                ? null
                : state.score.PLAYER_A > state.score.PLAYER_B
                    ? "PLAYER_A"
                    : "PLAYER_B";
            setFinishedMutable(state, winner, "time");
            finishedThisTick = "time";
        }
        if (scoredBy === null && finishedThisTick === null) {
            if (length(state.puck.velocity) < config.puck.stuckSpeed)
                state.puck.stationaryTicks += 1;
            else
                state.puck.stationaryTicks = 0;
            if (state.puck.stationaryTicks >= config.puck.stuckTicks && config.puck.stuckTicks > 0) {
                state.puck.position = { x: config.table.width / 2, y: config.table.height / 2 };
                state.puck.velocity = { x: 0, y: 0 };
                state.puck.stationaryTicks = 0;
                const resetEvent = { type: "PUCK_RESET", tick: state.tick, reason: "stuck" };
                events.push(resetEvent);
            }
        }
    }
    roundStateMutable(state, config);
    const stateHash = hashGameState(state);
    if (scoredBy !== null) {
        events.push({
            type: "GOAL",
            tick: state.tick,
            scorer: scoredBy,
            scoreA: state.score.PLAYER_A,
            scoreB: state.score.PLAYER_B,
            stateHash,
        });
    }
    if (finishedThisTick !== null) {
        events.push({
            type: "FINISHED",
            tick: state.tick,
            winner: state.winner,
            reason: finishedThisTick,
            scoreA: state.score.PLAYER_A,
            scoreB: state.score.PLAYER_B,
            stateHash,
        });
    }
    return {
        state,
        events,
        acceptedInputs: processed.accepted,
        rejectedInputs: processed.rejected,
        stateHash,
    };
}
/** Stateful convenience wrapper; `stepGame` remains the canonical pure transition. */
export class AuthoritativeGame {
    config;
    #state;
    #pendingInputs = [];
    constructor(options = {}) {
        this.config = resolveGameConfig(options.config);
        this.#state = createInitialState({ ...options, config: this.config });
    }
    get state() {
        return cloneGameState(this.#state);
    }
    get stateHash() {
        return hashGameState(this.#state);
    }
    setConnected(player, connected) {
        this.#state = setPlayerConnected(this.#state, player, connected);
        return this.state;
    }
    setReady(player, ready = true) {
        this.#state = setPlayerReady(this.#state, player, ready, this.config);
        return this.state;
    }
    queueInput(player, input) {
        const queued = {
            player,
            sequence: input.sequence,
            targetX: input.targetX,
            targetY: input.targetY,
            clientTime: input.clientTime,
        };
        this.#pendingInputs.push(queued);
    }
    step() {
        const pending = this.#pendingInputs;
        this.#pendingInputs = [];
        const result = stepGame(this.#state, pending, this.config);
        this.#state = result.state;
        return { ...result, state: this.state };
    }
    snapshot(ackSequence, serverTime) {
        return createSnapshotMessage(this.#state, {
            ...(ackSequence === undefined ? {} : { ackSequence }),
            ...(serverTime === undefined ? {} : { serverTime }),
        });
    }
    pause() {
        this.#state = pauseMatch(this.#state);
        return this.state;
    }
    resume() {
        this.#state = resumeMatch(this.#state);
        return this.state;
    }
    forfeit(player) {
        this.#state = forfeitMatch(this.#state, player);
        return this.state;
    }
    resetRound() {
        this.#state = resetRound(this.#state, this.config);
        return this.state;
    }
}
//# sourceMappingURL=simulation.js.map